/**
 * Bridges the auth lifecycle to the Zustand persist scope.
 *
 * The itinerary store is namespaced per user via `scopedEncryptedStorage`.
 * This module wraps the three transitions that have to happen atomically
 * for that namespacing to be safe:
 *
 *  - **Cold start** (`initStorageScopeOnBoot`). Read the stored access
 *    token, set the scope to match, then trigger the first rehydrate.
 *    Done before `setAuthFromTokens` so the persist middleware reads
 *    from the correct key on first try.
 *
 *  - **Sign in / sign up** (`claimAndSwitchToUser`). User was anonymous
 *    a moment ago — sweep any non-demo trips out of the anonymous blob,
 *    switch scope to the new user, rehydrate, then import the snatched
 *    trips so SyncManager uploads them.
 *
 *  - **Sign out** (`switchToAnonymous`). Flush pending writes under the
 *    user scope, drop the anonymous blob, switch back to anonymous,
 *    rehydrate (now a clean slate) so the next visitor on a shared
 *    device starts fresh.
 *
 * The persist middleware's custom `merge` (see `itinerary-store.ts`)
 * resets every persisted key to its default before applying the stored
 * values, so a plain `persist.rehydrate()` is sufficient — no
 * destructive pre-rehydrate setState is needed.
 */
import { useItineraryStore } from '../store/itinerary-store';
import { clearConsentForLogout } from '../store/consent-store';
import { useTourStore } from '../store/tour-store';
import {
  clearAnonymousScope,
  readAnonymousRaw,
  setStorageScope,
} from './scoped-encrypted-storage';
import { SyncManager } from './sync-manager';
import { UserPrefsSync } from './user-prefs-sync';
import { ensureDemoTripForAnonymous } from './sample-itinerary';
import { setPendingPrefsMigration, type PendingPrefsMigration } from './pending-prefs-migration';
import type {
  LayerPrefs,
  ReviewLayerPrefs,
} from '../store/slices/layers-slice';
import type { Trip, CardTag, LayerPrefRecord } from '@my-fullstack-app/itinerary-types';

const STORAGE_KEY = 'itinerary-trips';

interface PersistedAnonymousShape {
  state?: {
    trips?: Trip[];
    notesMap?: Record<string, string>;
    tagsMap?: Record<string, CardTag>;
    layerPrefs?: LayerPrefs;
    layerPrefsExplicit?: string[];
    reviewLayerPrefs?: ReviewLayerPrefs;
    reviewLayerPrefsExplicit?: string[];
    hapticsEnabled?: boolean;
  };
}

/** Pull the anonymous-session's explicit pref toggles from the raw
 *  blob so they can ride into a newly-signed-in user's scope. Per
 *  ADR-0082, only ids in the explicit set get migrated — values that
 *  happen to match the registry default never make it across, so a
 *  later registry-default change reaches the user untouched.
 *
 *  `UserPrefsSync.hydrate` consumes the returned stash AFTER the
 *  server has been queried; the per-explicit-id merge runs there. */
function extractAnonPrefs(raw: string | null): PendingPrefsMigration {
  if (!raw) return {};
  let parsed: PersistedAnonymousShape;
  try {
    parsed = JSON.parse(raw) as PersistedAnonymousShape;
  } catch {
    return {};
  }
  const s = parsed.state ?? {};
  const out: PendingPrefsMigration = {};
  const layerRecord = buildAnonLayerRecord(s.layerPrefsExplicit, s.layerPrefs);
  if (layerRecord) out.layerPrefs = layerRecord;
  const reviewRecord = buildAnonLayerRecord(s.reviewLayerPrefsExplicit, s.reviewLayerPrefs);
  if (reviewRecord) out.reviewLayerPrefs = reviewRecord;
  if (typeof s.hapticsEnabled === 'boolean') out.hapticsEnabled = s.hapticsEnabled;
  return out;
}

/** Compose an anon `LayerPrefRecord` from a persisted explicit array
 *  + values map. Returns `undefined` when the explicit array is
 *  missing or empty so the migration arm short-circuits cleanly. */
function buildAnonLayerRecord(
  explicit: string[] | undefined,
  values: Partial<Record<string, boolean>> | undefined,
): LayerPrefRecord | undefined {
  if (!Array.isArray(explicit) || explicit.length === 0) return undefined;
  const out: Record<string, boolean> = {};
  for (const id of explicit) {
    const v = values?.[id];
    if (typeof v === 'boolean') out[id] = v;
  }
  return { explicit: [...explicit], values: out };
}

/** Parse the raw persisted JSON for the anonymous scope and pull out the
 *  trips that should travel into a newly-signed-in user's scope. Drops
 *  the demo trip (`isDemo === true`) and any trip the user already has
 *  under their scope (defensive — the IDs are client-minted UUIDs so a
 *  collision is implausible, but cheap to guard). */
function extractClaimableTrips(
  raw: string | null,
  existingUserTripIds: Set<string>,
): { trips: Trip[]; notes: Record<string, string>; tags: Record<string, CardTag> } {
  if (!raw) return { trips: [], notes: {}, tags: {} };
  let parsed: PersistedAnonymousShape;
  try {
    parsed = JSON.parse(raw) as PersistedAnonymousShape;
  } catch {
    return { trips: [], notes: {}, tags: {} };
  }
  const anonymousTrips = parsed.state?.trips ?? [];
  const claimable = anonymousTrips.filter(
    (t) => t.isDemo !== true && !existingUserTripIds.has(t.id),
  );
  if (claimable.length === 0) return { trips: [], notes: {}, tags: {} };
  // Scope the notes / tags blobs to only the entities on claimed trips
  // so we don't drag in stale keys (or someone else's notes via a key
  // collision after a different user used the same browser anonymously).
  const entityIds = new Set<string>();
  for (const trip of claimable) {
    entityIds.add(trip.id);
    for (const day of trip.days) {
      entityIds.add(day.id);
      for (const p of day.places) entityIds.add(p.id);
      for (const t of day.transports ?? []) entityIds.add(t.id);
      for (const c of day.customs ?? []) entityIds.add(c.id);
    }
    for (const s of trip.stays ?? []) entityIds.add(s.id);
    for (const j of trip.journeys ?? []) entityIds.add(j.id);
  }
  const notes: Record<string, string> = {};
  for (const [k, v] of Object.entries(parsed.state?.notesMap ?? {})) {
    if (entityIds.has(k)) notes[k] = v;
  }
  const tags: Record<string, CardTag> = {};
  for (const [k, v] of Object.entries(parsed.state?.tagsMap ?? {})) {
    if (entityIds.has(k)) tags[k] = v;
  }
  return { trips: claimable, notes, tags };
}

/**
 * Cold-start hook. Sets the storage scope to match whichever user the
 * stored access token (if any) belongs to, then triggers the initial
 * persist rehydrate. Call from AuthContext's init effect AFTER it has
 * settled on a final token (post-refresh-attempt)
 * but BEFORE `setAuthFromTokens` writes the in-memory user — splash
 * stays up until rehydrate finishes so the user never sees a flash of
 * the wrong scope's content.
 */
export async function initStorageScopeOnBoot(userIdFromToken: string | null): Promise<void> {
  setStorageScope(userIdFromToken);
  await useItineraryStore.persist.rehydrate();
}

/**
 * Sign-in / sign-up hook. The user was anonymous a moment ago; pull any
 * non-demo trips out of the anonymous blob, switch scope to their userId,
 * rehydrate their own persisted data (if any), then import the claimed
 * trips so SyncManager uploads them. Clears the anonymous blob at the end
 * so the next visitor on a shared device starts clean.
 */
export async function claimAndSwitchToUser(userId: string): Promise<void> {
  // Read the anonymous blob BEFORE switching scope — once we flip
  // `scopedEncryptedStorage._scope` the lookup helpers would target
  // the user scope instead.
  const rawAnonymous = await readAnonymousRaw(STORAGE_KEY);

  // Stash anonymous-session pref choices BEFORE the rehydrate wipes
  // them. UserPrefsSync.hydrate (running in SyncManagerInit's
  // [isAuthenticated] effect post-sign-in) consumes them after the
  // server's record has been applied — server-wins per field, so a
  // brand-new account inherits the anonymous toggles while a returning
  // user on a new device keeps their existing server settings.
  setPendingPrefsMigration(extractAnonPrefs(rawAnonymous));

  setStorageScope(userId);
  await useItineraryStore.persist.rehydrate();

  const existing = new Set(useItineraryStore.getState().trips.map((t) => t.id));
  const { trips, notes, tags } = extractClaimableTrips(rawAnonymous, existing);
  if (trips.length > 0) {
    useItineraryStore.getState().importTrips(trips, {
      ...(Object.keys(notes).length > 0 ? { notes } : {}),
      ...(Object.keys(tags).length > 0 ? { tags } : {}),
    });
  }

  await clearAnonymousScope(STORAGE_KEY);
}

/**
 * Sign-out hook. Flush any pending writes under the user's scope, wipe
 * the consent decision, drop the anonymous blob, switch back to the
 * anonymous scope, then rehydrate (now guaranteed clean) and re-seed
 * the demo trip. Demo seeding is a no-op if a fresh anonymous user
 * immediately navigates to a route that already calls
 * `ensureDemoTripForAnonymous`, but doing it here keeps the trips list
 * non-empty in the worst case.
 */
export async function switchToAnonymous(): Promise<void> {
  // `allSettled` (not `all`): the explicit fail-fast of `all` would let
  // one manager's rejection short-circuit the other's flush. With
  // `allSettled` both flushes run to completion deterministically —
  // either both finish their network calls (token still valid) or both
  // bail through their own internal error handling.
  await Promise.allSettled([
    SyncManager.getInstance().flushImmediate(),
    UserPrefsSync.getInstance().flushImmediate(),
  ]);
  // Wipe the consent decision so the next visitor on a shared device
  // doesn't inherit it (visually or via the user-prefs-sync migration
  // arm). See `clearConsentForLogout` for the full reasoning.
  await clearConsentForLogout();
  // Reset the walkthrough run state too (ADR-0091) so a tour the
  // previous user was mid-way through doesn't bleed into the next
  // visitor on a shared device. The durable `tutorialDisabled` pref is
  // scope-reset by the persist `merge` below.
  useTourStore.getState().clearTourForLogout();
  // Wipe the anonymous slot BEFORE flipping scope + rehydrating, so the
  // rehydrate reads a clean blob. Without this, a stale anonymous blob
  // (trips from a pre-sign-in visit, or leftovers from a prior session
  // on a shared device) would leak back into the in-memory state and
  // then be re-persisted by `ensureDemoTripForAnonymous`'s setState.
  await clearAnonymousScope(STORAGE_KEY);
  setStorageScope(null);
  await useItineraryStore.persist.rehydrate();
  ensureDemoTripForAnonymous();
}
